import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Wallet } from '@festpix/services';
import { transferBalance } from '../../services/payment';
import { colors, spacing, borderRadius, typography, shadows } from '@festpix/ui';
import AttendeeLayout from '../../components/AttendeeLayout';

interface LocationState {
  childWallet: Wallet;
  parentWallet: Wallet;
}

const PRESET_AMOUNTS = [10, 20, 50, 100];

export default function TransferToChildScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const { childWallet, parentWallet } = (location.state as LocationState) || {};

  const [amount, setAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const numericAmount = parseFloat(amount.replace(',', '.')) || 0;
  const isValidAmount = numericAmount > 0 && numericAmount <= (parentWallet?.balance || 0);

  const handlePresetAmount = (value: number) => {
    setAmount(value.toString());
  };

  const handleTransfer = async () => {
    if (!isValidAmount) {
      setError('Por favor, insira um valor válido');
      return;
    }

    if (!parentWallet || !childWallet) {
      setError('Carteiras não encontradas');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await transferBalance(parentWallet.id, childWallet.id, numericAmount);
      setSuccess(true);
    } catch (err) {
      console.error('Error transferring balance:', err);
      setError(err instanceof Error ? err.message : 'Não foi possível realizar a transferência');
    } finally {
      setLoading(false);
    }
  };

  if (!parentWallet || !childWallet) {
    return (
      <AttendeeLayout showBackButton>
        <div style={styles.errorWrapper}>
          <p style={styles.errorText}>Dados invalidos. Volte e tente novamente.</p>
          <button style={styles.primaryButton} onClick={() => navigate('/')}>
            Voltar
          </button>
        </div>
      </AttendeeLayout>
    );
  }

  if (success) {
    return (
      <AttendeeLayout>
        <div style={styles.successContainer}>
          <div style={styles.successIcon}>
            <span style={styles.successEmoji}>✓</span>
          </div>
          <h1 style={styles.successTitle}>Transferência Realizada!</h1>
          <p style={styles.successMessage}>
            R$ {numericAmount.toFixed(2).replace('.', ',')} foi transferido para{' '}
            <strong>{childWallet.childName}</strong>
          </p>

          <div style={styles.summaryCard}>
            <div style={styles.summaryRow}>
              <span style={styles.summaryLabel}>Seu novo saldo:</span>
              <span style={styles.summaryValue}>
                R$ {(parentWallet.balance - numericAmount).toFixed(2).replace('.', ',')}
              </span>
            </div>
            <div style={styles.summaryDivider} />
            <div style={styles.summaryRow}>
              <span style={styles.summaryLabel}>Novo saldo de {childWallet.childName}:</span>
              <span style={styles.summaryValue}>
                R$ {(childWallet.balance + numericAmount).toFixed(2).replace('.', ',')}
              </span>
            </div>
          </div>

          <button
            style={{ ...styles.primaryButton, width: '100%', maxWidth: 360 }}
            onClick={() => navigate('/')}
          >
            Voltar
          </button>
        </div>
      </AttendeeLayout>
    );
  }

  return (
    <AttendeeLayout showBackButton>
      <div style={styles.scrollContent}>
        {/* Child Info Header */}
        <div style={styles.childHeader}>
          <div style={styles.childAvatar}>
            <span style={styles.childAvatarEmoji}>👦</span>
          </div>
          <h1 style={styles.childName}>{childWallet.childName || 'Filho'}</h1>
          <p style={styles.childBalance}>
            Saldo atual: R$ {childWallet.balance.toFixed(2).replace('.', ',')}
          </p>
        </div>

        {/* Your Balance Info */}
        <div style={styles.balanceInfo}>
          <span style={styles.balanceLabel}>Seu saldo disponível</span>
          <span style={styles.balanceAmount}>
            R$ {parentWallet.balance.toFixed(2).replace('.', ',')}
          </span>
        </div>

        {/* Amount Input */}
        <div style={styles.inputSection}>
          <label style={styles.inputLabel}>Valor a transferir</label>
          <div style={styles.inputContainer}>
            <span style={styles.currencyPrefix}>R$</span>
            <input
              style={styles.input}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              type="text"
              inputMode="decimal"
              placeholder="0,00"
            />
          </div>
        </div>

        {/* Preset Amounts */}
        <div style={styles.presetContainer}>
          <p style={styles.presetLabel}>Valores rápidos</p>
          <div style={styles.presetGrid}>
            {PRESET_AMOUNTS.map((value) => {
              const isDisabled = value > parentWallet.balance;
              const isActive = amount === value.toString();
              return (
                <button
                  key={value}
                  style={{
                    ...styles.presetButton,
                    ...(isActive ? styles.presetButtonActive : {}),
                    ...(isDisabled ? styles.presetButtonDisabled : {}),
                  }}
                  onClick={() => !isDisabled && handlePresetAmount(value)}
                  disabled={isDisabled}
                >
                  <span style={{
                    ...styles.presetButtonText,
                    ...(isActive ? styles.presetButtonTextActive : {}),
                    ...(isDisabled ? styles.presetButtonTextDisabled : {}),
                  }}>
                    R$ {value}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Error Message */}
        {numericAmount > parentWallet.balance && (
          <div style={styles.errorContainer}>
            <p style={styles.errorContainerText}>
              Saldo insuficiente. Seu saldo é R$ {parentWallet.balance.toFixed(2).replace('.', ',')}
            </p>
          </div>
        )}

        {error && <p style={styles.errorText}>{error}</p>}

        {/* Transfer Summary */}
        {isValidAmount && (
          <div style={styles.transferSummary}>
            <p style={styles.summaryText}>
              Após a transferência, {childWallet.childName} terá:
            </p>
            <p style={styles.summaryNewBalance}>
              R$ {(childWallet.balance + numericAmount).toFixed(2).replace('.', ',')}
            </p>
          </div>
        )}

        {/* Transfer Button */}
        <button
          style={{
            ...styles.primaryButton,
            ...(!isValidAmount ? styles.primaryButtonDisabled : {}),
          }}
          onClick={handleTransfer}
          disabled={!isValidAmount || loading}
        >
          {loading
            ? 'Transferindo...'
            : `Transferir R$ ${numericAmount > 0 ? numericAmount.toFixed(2).replace('.', ',') : '0,00'}`
          }
        </button>

        {/* Cancel Button */}
        <button
          style={styles.cancelButton}
          onClick={() => navigate('/')}
          disabled={loading}
        >
          Cancelar
        </button>
      </div>
    </AttendeeLayout>
  );
}

const styles: { [key: string]: React.CSSProperties } = {
  errorWrapper: {
    textAlign: 'center',
    padding: spacing.xl,
  },
  container: {
    minHeight: '100vh',
    backgroundColor: colors.background,
  },
  scrollContent: {
    padding: spacing.lg,
    paddingBottom: spacing.xl,
  },

  // Child Header
  childHeader: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginBottom: spacing.xl,
  },
  childAvatar: {
    width: 80,
    height: 80,
    borderRadius: 40,
    backgroundColor: colors.infoBg,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: spacing.md,
  },
  childAvatarEmoji: {
    fontSize: 40,
  },
  childName: {
    fontSize: typography.fontSize['2xl'],
    fontWeight: typography.fontWeight.bold,
    color: colors.text,
    marginBottom: spacing.xs,
    margin: 0,
  },
  childBalance: {
    fontSize: typography.fontSize.md,
    color: colors.textSecondary,
    margin: 0,
    marginTop: spacing.xs,
  },

  // Balance Info
  balanceInfo: {
    backgroundColor: colors.cardBackground,
    padding: spacing.md,
    borderRadius: borderRadius.lg,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginBottom: spacing.lg,
    boxShadow: shadows.sm,
  },
  balanceLabel: {
    fontSize: typography.fontSize.sm,
    color: colors.textSecondary,
    marginBottom: spacing.xs,
  },
  balanceAmount: {
    fontSize: typography.fontSize.xl,
    fontWeight: typography.fontWeight.bold,
    color: colors.primary,
  },

  // Input Section
  inputSection: {
    marginBottom: spacing.lg,
  },
  inputLabel: {
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.medium,
    color: colors.textSecondary,
    marginBottom: spacing.sm,
    display: 'block',
  },
  inputContainer: {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: colors.cardBackground,
    borderRadius: borderRadius.lg,
    border: `2px solid ${colors.border}`,
    padding: `0 ${spacing.md}px`,
    boxShadow: shadows.sm,
  },
  currencyPrefix: {
    fontSize: typography.fontSize['2xl'],
    fontWeight: typography.fontWeight.bold,
    color: colors.textSecondary,
    marginRight: spacing.sm,
  },
  input: {
    flex: 1,
    fontSize: typography.fontSize['3xl'],
    fontWeight: typography.fontWeight.bold,
    color: colors.text,
    padding: `${spacing.md}px 0`,
    border: 'none',
    outline: 'none',
    backgroundColor: 'transparent',
  },

  // Preset Amounts
  presetContainer: {
    marginBottom: spacing.lg,
  },
  presetLabel: {
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.medium,
    color: colors.textSecondary,
    marginBottom: spacing.sm,
    margin: 0,
  },
  presetGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: spacing.sm,
    marginTop: spacing.sm,
  },
  presetButton: {
    backgroundColor: colors.cardBackground,
    padding: spacing.md,
    borderRadius: borderRadius.md,
    border: `1px solid ${colors.border}`,
    cursor: 'pointer',
  },
  presetButtonActive: {
    backgroundColor: colors.primary,
    borderColor: colors.primary,
  },
  presetButtonDisabled: {
    backgroundColor: colors.neutral100,
    borderColor: colors.neutral200,
    cursor: 'not-allowed',
  },
  presetButtonText: {
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.semibold,
    color: colors.text,
  },
  presetButtonTextActive: {
    color: colors.textLight,
  },
  presetButtonTextDisabled: {
    color: colors.textDisabled,
  },

  // Error
  errorContainer: {
    backgroundColor: colors.errorBg,
    padding: spacing.md,
    borderRadius: borderRadius.md,
    marginBottom: spacing.lg,
  },
  errorContainerText: {
    fontSize: typography.fontSize.sm,
    color: colors.error,
    textAlign: 'center',
    margin: 0,
  },
  errorText: {
    fontSize: typography.fontSize.sm,
    color: colors.error,
    textAlign: 'center',
    marginBottom: spacing.md,
  },

  // Transfer Summary
  transferSummary: {
    backgroundColor: colors.successBg,
    padding: spacing.md,
    borderRadius: borderRadius.md,
    marginBottom: spacing.lg,
    textAlign: 'center',
  },
  summaryText: {
    fontSize: typography.fontSize.sm,
    color: colors.success,
    marginBottom: spacing.xs,
    margin: 0,
  },
  summaryNewBalance: {
    fontSize: typography.fontSize.xl,
    fontWeight: typography.fontWeight.bold,
    color: colors.success,
    margin: 0,
    marginTop: spacing.xs,
  },

  // Buttons
  primaryButton: {
    backgroundColor: colors.primary,
    padding: spacing.md,
    borderRadius: borderRadius.md,
    color: colors.textLight,
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.semibold,
    width: '100%',
    marginBottom: spacing.md,
    boxShadow: shadows.colored,
    border: 'none',
    cursor: 'pointer',
  },
  primaryButtonDisabled: {
    backgroundColor: colors.neutral400,
    boxShadow: 'none',
    cursor: 'not-allowed',
  },
  cancelButton: {
    padding: spacing.md,
    backgroundColor: 'transparent',
    color: colors.textSecondary,
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.medium,
    width: '100%',
    border: 'none',
    cursor: 'pointer',
  },

  // Success Screen
  successContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    padding: spacing.lg,
  },
  successIcon: {
    width: 100,
    height: 100,
    borderRadius: 50,
    backgroundColor: colors.success,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: spacing.lg,
  },
  successEmoji: {
    fontSize: 50,
    color: colors.textLight,
  },
  successTitle: {
    fontSize: typography.fontSize['2xl'],
    fontWeight: typography.fontWeight.bold,
    color: colors.text,
    marginBottom: spacing.sm,
    margin: 0,
  },
  successMessage: {
    fontSize: typography.fontSize.md,
    color: colors.textSecondary,
    textAlign: 'center',
    marginBottom: spacing.xl,
    margin: 0,
    marginTop: spacing.sm,
  },
  summaryCard: {
    backgroundColor: colors.cardBackground,
    borderRadius: borderRadius.lg,
    padding: spacing.lg,
    width: '100%',
    maxWidth: 360,
    marginBottom: spacing.xl,
    boxShadow: shadows.md,
  },
  summaryRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  summaryLabel: {
    fontSize: typography.fontSize.sm,
    color: colors.textSecondary,
  },
  summaryValue: {
    fontSize: typography.fontSize.lg,
    fontWeight: typography.fontWeight.bold,
    color: colors.success,
  },
  summaryDivider: {
    height: 1,
    backgroundColor: colors.border,
    margin: `${spacing.md}px 0`,
  },
};
